import { useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import type { Upload } from "tus-js-client";
import { useQueryClient } from "@tanstack/react-query";
import {
  createOperationalUploadSession,
  getOperationalUploadSession,
  operationalKeys,
  pb,
} from "@rekonas/data-access";
import type { OperationalUploadSession } from "@rekonas/domain";
import { Alert, Button, Progress, Spinner } from "@rekonas/ui";
import { createTusUpload, findMatchingPreviousUpload } from "./tusUpload";

type TransferState = "preparing" | "discovering" | "uploading" | "paused" | "finalizing" | "accepted" | "error";

interface Transfer {
  state: TransferState;
  file: File;
  participantRef: string;
  idempotencyKey: string;
  session: OperationalUploadSession | null;
  progress: number;
  resumed: boolean;
  error?: string;
}

export interface UploadPanelProps {
  workspaceRef: string;
  participantRefs: string[];
}

export function isAcquisitionArchive(file: File): boolean {
  return file.size > 0 && /^[A-Za-z0-9][A-Za-z0-9_.-]{0,235}\.tgz$/.test(file.name);
}

function fileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function uploadKey(workspaceRef: string, participantRef: string, file: File): string {
  return `rekonas-operational-upload:${workspaceRef}:${participantRef}:${file.name}:${file.size}:${file.lastModified}`;
}

export function durableIdempotencyKey(workspaceRef: string, participantRef: string, file: File): string {
  const storageKey = uploadKey(workspaceRef, participantRef, file);
  const retained = window.localStorage.getItem(storageKey);
  if (retained) return retained;
  const value = `web-${crypto.randomUUID()}`;
  window.localStorage.setItem(storageKey, value);
  return value;
}

const delay = (milliseconds: number) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

export function UploadPanel({ workspaceRef, participantRefs }: UploadPanelProps) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const activeUpload = useRef<Upload | null>(null);
  const finalizationRequest = useRef<AbortController | null>(null);
  const uniqueParticipants = useMemo(() => [...new Set(participantRefs)].sort(), [participantRefs]);
  const [participantRef, setParticipantRef] = useState(uniqueParticipants.length === 1 ? uniqueParticipants[0] : "");
  const [transfer, setTransfer] = useState<Transfer | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  useEffect(() => {
    setParticipantRef((current) => {
      if (uniqueParticipants.length === 1) return uniqueParticipants[0];
      return current && uniqueParticipants.includes(current) ? current : "";
    });
  }, [uniqueParticipants]);
  useEffect(() => () => {
    finalizationRequest.current?.abort();
    void activeUpload.current?.abort(false).catch(() => undefined);
  }, []);

  async function waitForAcceptance(uploadRef: string) {
    finalizationRequest.current?.abort();
    const controller = new AbortController();
    finalizationRequest.current = controller;
    for (let attempt = 0; attempt < 120; attempt += 1) {
      let session: OperationalUploadSession;
      try {
        session = await getOperationalUploadSession(uploadRef, controller.signal);
      } catch (error) {
        if (controller.signal.aborted) return;
        throw error;
      }
      setTransfer((current) => current ? { ...current, session, state: session.state === "accepted" ? "accepted" : "finalizing" } : current);
      if (session.state === "accepted") {
        await queryClient.invalidateQueries({ queryKey: operationalKeys.list(workspaceRef) });
        return;
      }
      if (["rejected", "conflict", "expired"].includes(session.state)) {
        throw new Error(session.error?.code || `Upload ${session.state}`);
      }
      await delay(Math.min(5_000, 750 + attempt * 250));
    }
    throw new Error("The archive was uploaded, but final acceptance is still pending. Retry to check again.");
  }

  async function begin(file: File, session: OperationalUploadSession) {
    setTransfer((current) => current ? { ...current, state: "discovering", session, error: undefined } : current);
    const upload = createTusUpload({
      session,
      token: pb.authStore.token,
      file,
      onError: (error) => setTransfer((current) => current ? { ...current, state: "error", error: error.message } : current),
      onProgress: (uploaded, total) => setTransfer((current) => current ? {
        ...current, state: "uploading", progress: total > 0 ? (uploaded / total) * 100 : 0,
      } : current),
      onSuccess: () => {
        setTransfer((current) => current ? { ...current, state: "finalizing", progress: 100 } : current);
        void waitForAcceptance(session.upload_ref).catch((error) => setTransfer((current) => current ? {
          ...current, state: "error", error: error instanceof Error ? error.message : "Finalization status is unavailable.",
        } : current));
      },
    });
    activeUpload.current = upload;
    try {
      const previous = session.tus.upload_url ? [] : await upload.findPreviousUploads();
      const match = findMatchingPreviousUpload(previous, session.upload_ref);
      if (match) upload.resumeFromPreviousUpload(match);
      setTransfer((current) => current ? {
        ...current, state: "uploading", resumed: Boolean(match || session.tus.upload_url), error: undefined,
      } : current);
      upload.start();
    } catch (error) {
      setTransfer((current) => current ? {
        ...current, state: "error", error: error instanceof Error ? error.message : "Could not inspect resumable uploads.",
      } : current);
    }
  }

  async function prepare(file: File, selectedParticipant: string, idempotencyKey: string) {
    setTransfer({ state: "preparing", file, participantRef: selectedParticipant, idempotencyKey,
      session: null, progress: 0, resumed: false });
    try {
      const session = await createOperationalUploadSession({
        workspace_ref: workspaceRef,
        participant_ref: selectedParticipant,
        original_filename: file.name,
        size_bytes: file.size,
        idempotency_key: idempotencyKey,
      });
      await begin(file, session);
    } catch (error) {
      setTransfer((current) => current ? {
        ...current, state: "error", error: error instanceof Error ? error.message : "Could not create the upload session.",
      } : current);
    }
  }

  function acceptFile(file: File | undefined) {
    setValidationError(null);
    if (!file) return;
    if (!isAcquisitionArchive(file)) {
      setValidationError("Choose one non-empty .tgz acquisition archive. EDF and RKNS files are not accepted here.");
      return;
    }
    if (!participantRef) {
      setValidationError("Choose an authorized participant binding for this archive.");
      return;
    }
    void prepare(file, participantRef, durableIdempotencyKey(workspaceRef, participantRef, file));
  }

  function handleFile(event: ChangeEvent<HTMLInputElement>) {
    acceptFile(event.target.files?.[0]);
    event.target.value = "";
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    acceptFile(event.dataTransfer.files?.[0]);
  }

  async function pause() {
    if (!activeUpload.current) return;
    await activeUpload.current.abort(false);
    setTransfer((current) => current ? { ...current, state: "paused" } : current);
  }

  function retry() {
    if (!transfer) return;
    if (transfer.session?.state === "finalizing") {
      setTransfer((current) => current ? { ...current, state: "finalizing", error: undefined } : current);
      void waitForAcceptance(transfer.session.upload_ref).catch((error) => setTransfer((current) => current ? {
        ...current, state: "error", error: error instanceof Error ? error.message : "Finalization status is unavailable.",
      } : current));
    } else if (transfer.session) void begin(transfer.file, transfer.session);
    else void prepare(transfer.file, transfer.participantRef, transfer.idempotencyKey);
  }

  const busy = Boolean(transfer && ["preparing", "discovering", "uploading", "finalizing"].includes(transfer.state));
  return (
    <section className="test-tools-panel" aria-labelledby="test-upload-title">
      <header><p>Phase-A acquisition path</p><h3 id="test-upload-title">Upload recording archive</h3>
        <span>Operator test tool · accepted recordings enter the normal processing flow.</span></header>
      {uniqueParticipants.length > 1 && <label className="test-tools-participant"><span>Participant binding</span>
        <select value={participantRef} onChange={(event) => setParticipantRef(event.target.value)} disabled={busy}>
          <option value="">Choose participant…</option>
          {uniqueParticipants.map((ref) => <option key={ref} value={ref}>{ref}</option>)}
        </select></label>}
      {uniqueParticipants.length === 0 &&
        <Alert>No enrolled upload participant is available for this account. Ask an operator to provision uploader access.</Alert>}
      <>
        <div className="test-upload-dropzone" onDragOver={(event) => event.preventDefault()} onDrop={handleDrop}>
          <input ref={fileInput} className="sr-only" type="file" accept=".tgz,application/gzip" onChange={handleFile} disabled={busy || uniqueParticipants.length === 0} />
          <strong>Drop a .tgz acquisition archive</strong><span>or choose the same archive format exported by the mobile workflow</span>
          <Button size="sm" variant="secondary" onClick={() => fileInput.current?.click()} disabled={busy || uniqueParticipants.length === 0}>{busy ? <Spinner /> : null}Choose .tgz</Button>
        </div>
        {validationError && <Alert>{validationError}</Alert>}
        {transfer && <div className="test-upload-transfer" aria-live="polite">
          <div><strong>{transfer.file.name}</strong><span>{fileSize(transfer.file.size)}</span></div>
          <p>
            {transfer.state === "preparing" && "Preparing authorized upload session…"}
            {transfer.state === "discovering" && "Checking for a resumable browser transfer…"}
            {transfer.state === "uploading" && `${transfer.resumed ? "Resumed · " : ""}${Math.round(transfer.progress)}% uploaded`}
            {transfer.state === "paused" && "Paused safely in this browser."}
            {transfer.state === "finalizing" && "Archive transferred · awaiting server acceptance…"}
            {transfer.state === "accepted" && "Accepted · the recording now follows normal Operational processing."}
            {transfer.state === "error" && "Upload needs attention."}
          </p>
          {["uploading", "paused", "finalizing", "accepted"].includes(transfer.state) && <Progress value={transfer.progress} />}
          {transfer.state === "uploading" && <Button size="sm" variant="ghost" onClick={() => void pause()}>Pause</Button>}
          {(transfer.state === "paused" || transfer.state === "error") && <Button size="sm" variant="secondary" onClick={retry}>Retry / resume</Button>}
          {transfer.state === "error" && transfer.error && <Alert>{transfer.error}</Alert>}
        </div>}
      </>
    </section>
  );
}
